import { ForwardedRef, forwardRef, useId } from "react";

import {
  Box,
  CircularProgress,
  FormHelperText,
  Autocomplete as MUIAutocomplete,
  MenuItem,
} from "@mui/material";

import { useAutocomplete } from "./Autocomplete.hooks";
import {
  Container,
  Input,
  SelectedIconWrapper,
  StyledPopper,
} from "./Autocomplete.styles";
import {
  AutocompleteProps,
  AutocompleteValue,
  AutocompleteReference,
} from "./Autocomplete.types";
import { CheckIcon, ChevronDownIcon, XIcon } from "../Icon";
import InputLabel from "../InputLabel/InputLabel";

const AutocompleteInner = <V, R>(
  {
    color,
    defaultValue,
    disabled,
    error,
    helperText,
    id,
    inputRef,
    label,
    loading,
    options = [],
    placeholder,
    prefix,
    readOnly,
    required,
    suffix,
    value,
    renderOption,
    onChange,
    onInputChange,
  }: AutocompleteProps<AutocompleteValue<V>>,
  ref: ForwardedRef<R>
) => {
  const { autocompleteRef, valueState, onChangeHandler } = useAutocomplete({
    ref,
    defaultValue,
    value,
    onChange,
  });
  const formId = useId();

  return (
    <Container>
      {label && (
        <InputLabel htmlFor={id ?? formId} required={required}>
          {label}
        </InputLabel>
      )}
      <MUIAutocomplete
        fullWidth
        disablePortal
        ref={autocompleteRef}
        id={id ?? formId}
        color={color}
        disabled={disabled}
        readOnly={readOnly}
        loading={loading}
        options={options}
        value={valueState}
        popupIcon={<ChevronDownIcon fontSize="small" />}
        clearIcon={<XIcon fontSize="small" />}
        isOptionEqualToValue={(option, v) => option.value === v.value}
        onChange={(_event, selectedValue) => onChangeHandler(selectedValue)}
        onInputChange={(_e, v) => onInputChange?.(v)}
        renderOption={(props, option, state) => (
          <MenuItem {...props}>
            {typeof renderOption === "function"
              ? renderOption(props, option, state)
              : option.label}
            {state.selected ? (
              <SelectedIconWrapper>
                <CheckIcon color="primary" fontSize="small" />
              </SelectedIconWrapper>
            ) : null}
          </MenuItem>
        )}
        renderInput={(params) => (
          <Input
            {...params}
            color={color}
            inputRef={inputRef}
            required={required}
            disabled={disabled}
            variant="outlined"
            size="small"
            placeholder={placeholder}
            error={error}
            InputProps={{
              ...params.InputProps,
              startAdornment: prefix ? (
                <Box
                  display="inline-flex"
                  alignItems="center"
                  marginLeft="8px"
                  marginRight="6px"
                >
                  {prefix}
                </Box>
              ) : null,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={14} />
                  ) : null}
                  {suffix}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        PopperComponent={StyledPopper}
      />
      {helperText && (
        <FormHelperText disabled={disabled} error={error}>
          {helperText}
        </FormHelperText>
      )}
    </Container>
  );
};

const Autocomplete = forwardRef(AutocompleteInner) as <V, D>(
  props: AutocompleteProps<AutocompleteValue<V, D>> & {
    ref?: ForwardedRef<AutocompleteReference<AutocompleteValue<V, D>>>;
  }
) => ReturnType<typeof AutocompleteInner>;

export default Autocomplete;
